import React, { useEffect, useState } from 'react';

import Button from 'CommonComponents/Button';

import { useConfirmationModal } from 'Hooks/useConfirmationModal';
import { useToast } from 'Hooks/useToast';

import ExclamationTriangleIcon from 'Assets/icons/exclamation-triangle.svg';
import GlobeIcon from 'Assets/icons/globe-asia-australia.svg';
import StarIcon from 'Assets/icons/star.svg';
import TrashIcon from 'Assets/icons/trash.svg';

/**
 * Saved-sets row for the picker (IB-641): lists the current user's own and any
 * org-published sets, applies one into the draft, saves the current draft as a
 * new set, and (for admins) publishes a set to the whole organisation.
 *
 * Applying a set warns when some — or all — of its projects are archived, since
 * archived projects fall outside the live selection unless "Show archived" is on.
 */
export default function SavedSetsRow({ savedSetsUrl, isAdmin, draft, events, onApplySelection }) {
    const toast = useToast();
    const confirm = useConfirmationModal();
    const [sets, setSets] = useState([]);
    const [saving, setSaving] = useState(false);
    const [showSave, setShowSave] = useState(false);
    const [newName, setNewName] = useState('');

    useEffect(() => {
        fetchSets();
    }, []);

    const fetchSets = () => {
        axios
            .get(savedSetsUrl)
            .then((res) => setSets(res.data || []))
            .catch((e) => console.error('Failed to fetch saved project sets:', e));
    };

    const archivedIdSet = new Set(events.filter((e) => e.archived).map((e) => e.id));
    const liveIdSet = new Set(events.map((e) => e.id));

    // Build and commit the next draft from the set selection.
    const commitSetSelection = (set) => {
        const next = {};
        events.forEach((event) => {
            const saved = set.selection?.[event.id];
            const enabled = !!saved && !event.archived;
            next[event.id] = {
                enabled,
                budget: saved && saved.budget != null ? saved.budget : (draft[event.id]?.budget ?? null),
            };
        });
        onApplySelection(next);
    };

    const applySet = (set) => {
        const selectionIds = Object.keys(set.selection || {}).map((id) => Number(id));
        const known = selectionIds.filter((id) => liveIdSet.has(id));
        const archivedInSet = known.filter((id) => archivedIdSet.has(id));

        if (known.length === 0) {
            // Informational only — every project in the set is archived.
            confirm({
                title: $t('All projects archived'),
                message: $t('Every project in this set is archived and cannot be shown without "Show archived".'),
                actions: [{ label: $t('OK'), variant: 'primary' }],
            });

            return;
        }

        if (archivedInSet.length > 0) {
            confirm({
                title: $t('Archived projects in set'),
                message: $t('{{count}} project(s) in this set are archived. They will be skipped unless "Show archived" is enabled. Apply anyway?', {
                    count: archivedInSet.length,
                }),
                onConfirm: () => commitSetSelection(set),
            });

            return;
        }

        commitSetSelection(set);
    };

    const saveCurrent = () => {
        const name = newName.trim();
        if (!name) {
            return;
        }

        const selection = {};
        events.forEach((event) => {
            if (draft[event.id]?.enabled) {
                selection[event.id] = { budget: draft[event.id].budget ?? null };
            }
        });

        setSaving(true);
        axios
            .post(savedSetsUrl, { name, selection })
            .then(() => {
                setNewName('');
                setShowSave(false);
                fetchSets();
            })
            .catch(() => toast.error($t('Failed to save set.')))
            .finally(() => setSaving(false));
    };

    const publishSet = (set) => {
        axios
            .post(`${savedSetsUrl}/${set.id}/publish`)
            .then(() => fetchSets())
            .catch(() => toast.error($t('Failed to publish set.')));
    };

    const deleteSet = (set) => {
        confirm({
            title: $t('Delete saved set'),
            message: $t('Are you sure you want to delete this saved set?'),
            onConfirm: () => {
                axios
                    .delete(`${savedSetsUrl}/${set.id}`)
                    .then(() => fetchSets())
                    .catch(() => toast.error($t('Failed to delete set.')));
            },
        });
    };

    return (
        <div className="tw-flex tw-flex-col tw-gap-2">
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                <span className="tw-text-xs tw-font-medium tw-text-charcoal-75 tw-me-1">{$t('Saved sets')}</span>

                {sets.length === 0 && <span className="tw-text-xs tw-text-charcoal-50">{$t('No saved sets yet')}</span>}

                {sets.map((set) => (
                    <span
                        key={set.id}
                        className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-medium tw-bg-blue-10 tw-text-blue"
                    >
                        <button type="button" onClick={() => applySet(set)} className="tw-inline-flex tw-items-center tw-gap-1">
                            {set.is_published && <GlobeIcon className="tw-w-3 tw-h-3" />}
                            {set.name}
                        </button>
                        {isAdmin && !set.is_published && (
                            <button
                                type="button"
                                aria-label={$t('Publish to organisation')}
                                title={$t('Publish to organisation')}
                                onClick={() => publishSet(set)}
                            >
                                <GlobeIcon className="tw-w-3 tw-h-3" />
                            </button>
                        )}
                        {(set.is_mine || isAdmin) && (
                            <button type="button" aria-label={$t('Delete saved set')} title={$t('Delete saved set')} onClick={() => deleteSet(set)}>
                                <TrashIcon className="tw-w-3 tw-h-3" />
                            </button>
                        )}
                    </span>
                ))}

                <button
                    type="button"
                    onClick={() => setShowSave((prev) => !prev)}
                    className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-text-blue tw-font-medium"
                >
                    <StarIcon className="tw-w-3 tw-h-3" />
                    {$t('Save current')}
                </button>
            </div>

            {showSave && (
                <div className="tw-flex tw-items-center tw-gap-2">
                    <input
                        type="text"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        placeholder={$t('Name this set')}
                        className="tw-flex-1 tw-border-2 tw-border-grey tw-rounded-lg tw-text-sm tw-px-3 tw-py-1.5 tw-text-charcoal"
                    />
                    <Button variant="primary" small loading={saving} onClick={saveCurrent}>
                        {$t('Save')}
                    </Button>
                </div>
            )}

            {events.length > 0 && events.every((e) => e.archived) && (
                <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-2xs tw-text-yellow-400">
                    <ExclamationTriangleIcon className="tw-w-3.5 tw-h-3.5" />
                    {$t('All visible projects are archived.')}
                </div>
            )}
        </div>
    );
}
